import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useSearchParams,
  Navigate
} from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { BuildingsPage } from './pages/BuildingsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { InvoicesPageNew } from './pages/InvoicesPageNew';
import { RecipesPage } from './pages/RecipesPage';
import { RecipesPageNew } from './pages/RecipesPageNew';
import { Scope1DjegjaPage } from './pages/Scope1DjegjaPage';
import { Scope1MobilePage } from './pages/Scope1MobilePage';
import { Scope1ProcessPage } from './pages/Scope1ProcessPage';
import { Scope1FugitivePage } from './pages/Scope1FugitivePage';
import { Scope2ProcessPage } from './pages/Scope2ProcessPage';
import { Scope3Page } from './pages/Scope3Page';
import UiKitPage from './pages/UiKitPage';
import AccountPage from './pages/AccountPage';
import OverviewPage from './pages/OverviewPage';
import ActionInboxPage from './pages/ActionInboxPage';
import RecordsPage from './pages/RecordsPage';
import DataQualityPage from './pages/DataQualityPage';
import ExplorerPage from './pages/ExplorerPage';
import FacilitiesPage from './pages/FacilitiesPage';
import EmissionsDashboard from './pages/EmissionsDashboard';
import DetailedReportPage from './pages/DetailedReportPage';
import { MonthlyFlowPage } from './pages/MonthlyFlowPage';
import BadgePage from './pages/BadgePage';
import RegistrationWizard from './pages/RegistrationWizard';
import AdminTokensPage from './pages/AdminTokensPage';
import AdminUsersPage from './pages/AdminUsersPage';
import CbamOverviewPage from './pages/cbam/CbamOverviewPage';
import CbamInstallationsPage from './pages/cbam/CbamInstallationsPage';
import CbamGoodsPage from './pages/cbam/CbamGoodsPage';
import { ProtectedRoute } from './components/ProtectedRoute.jsx';
import { setAuthToken } from './services/api';
import { AuthBranding } from './components/AuthBranding.jsx';
import { useT } from './i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

function Login() {
  const { t } = useT();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');
  const navigate = useNavigate();

  // Check if user was redirected due to session expiration
  useEffect(() => {
    const expired = sessionStorage.getItem('sessionExpired');
    if (expired) {
      setSessionExpiredMsg(t('appx.sessionExpired'));
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSessionExpiredMsg('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/login`, { username, password });
      const token = res.data.token;
      localStorage.setItem('token', token);
      setAuthToken(token); // Configure Axios to send token with all requests
      // Returning users land on Overview. First-time users still go through the
      // registration wizard, which is a separate route reached at sign-up.
      navigate('/overview');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check your credentials and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <AuthBranding />
      <div className="login-right">
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <h2>Log in to Karbon360°</h2>
          <p>Enter your username or email address and password.</p>
          {sessionExpiredMsg && (
            <div className="login-notice" role="alert">{sessionExpiredMsg}</div>
          )}
          <label htmlFor="login-username">Username or email address</label>
          <input
            id="login-username"
            type="text"
            name="username"
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
          />
          <label htmlFor="login-password">Password</label>
          <div className="password-field">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="password-toggle"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(s => !s)}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          {error && <div className="login-error" role="alert">{error}</div>}
          <button type="submit" className="login-submit" disabled={submitting}>
            {submitting ? 'Logging in…' : 'Log in'}
          </button>
          <div className="login-links">
            <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
            <Link to="/register" className="signup-link">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}


// Plans as published on karbon360.tech/pricing. Kept here so the signup screen
// can name the plan someone chose, rather than dropping them into a bare form.
const PLANS = {
  starter:    { name: 'Starter',              price: '\u20ac149 / month',  blurb: 'One facility, Scope 1, 2 and 3.' },
  growth:     { name: 'Growth',               price: '\u20ac349 / month',  blurb: 'Up to three facilities, AI document extraction.' },
  compliance: { name: 'Compliance & Export',  price: '\u20ac699 / month',  blurb: 'Full GHG inventory, CBAM readiness, audit trail.' },
};

/*
 * Public signup — English, because this page is the front door for everyone,
 * not only Albanian-speaking customers. The invite wizard stays Albanian: that
 * one is sent to a named person we already know.
 *
 * Two steps: details, then plan. POST /api/register records the person with
 * status 'pending' and their requested_plan; they cannot log in until an admin
 * activates them, so this promises follow-up, never immediate access.
 *
 * Payment is deliberately NOT wired up yet. The plan step captures the choice
 * and checkout slots in after it — until then, say plainly that we will be in
 * touch rather than implying a payment has been taken.
 */
function Register() {
  const [searchParams] = useSearchParams();
  const presetPlan = (searchParams.get('plan') || '').toLowerCase();
  const inviteToken = searchParams.get('token');

  const [step, setStep] = useState('details'); // details -> plan -> done
  const [plan, setPlan] = useState(PLANS[presetPlan] ? presetPlan : '');
  const [form, setForm] = useState({
    username: '', full_name: '', age: '', email: '', phone: '',
    occupation: '', company: '', company_size: '', country: '',
    password: '', marketing_opt_in: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  /*
   * An invited customer who arrives here with their token — because a rewritten
   * link lost its path, or because they reached for "Create an account" with the
   * token still on the URL — belongs in the wizard. This page would take their
   * details and leave them pending while a perfectly good invite went unused.
   */
  if (inviteToken) {
    return <Navigate to={`/register-wizard?token=${encodeURIComponent(inviteToken)}`} replace />;
  }

  const validateDetails = () => {
    const errors = [];
    if (!form.full_name.trim()) errors.push('Full name is required');
    if (!form.email.trim()) errors.push('Email is required');
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.push('Email is not valid');
    if (!form.username.trim()) errors.push('Username is required');
    else if (form.username.trim().length < 3) errors.push('Username must be at least 3 characters');
    if (!form.password) errors.push('Password is required');
    else if (form.password.length < 6) errors.push('Password must be at least 6 characters');
    if (form.age && isNaN(Number(form.age))) errors.push('Age must be a number');
    return errors;
  };

  const goToPlan = (e) => {
    e.preventDefault();
    setError('');
    const errors = validateDetails();
    setValidationErrors(errors);
    if (errors.length === 0) setStep('plan');
  };

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/register`, {
        ...form,
        age: form.age === '' ? undefined : Number(form.age),
        plan: plan || undefined
      });
      setStep('done');
    } catch (err) {
      // Show what the server actually said. The previous version collapsed every
      // failure into a bare "Registration failed", which told nobody anything.
      const res = err.response;
      const fieldErrors = res?.data?.errors?.map((x) => x.msg).filter(Boolean);
      if (fieldErrors && fieldErrors.length) {
        setValidationErrors(fieldErrors);
        setStep('details');
      } else {
        setError(res?.data?.message || 'Could not submit your request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="login-page">
        <AuthBranding />
        <div className="login-right">
          <div className="login-form">
            <h2>Request received</h2>
            <p className="register-intro">
              Thanks, {form.full_name.split(' ')[0] || 'there'}. We will set up your
              workspace with the right emission factors for your country and email you
              a link to finish creating your account.
              {plan ? ` We have noted your interest in the ${PLANS[plan].name} plan, and will confirm pricing with you before anything is charged.` : ''}
            </p>
            <p className="register-note">
              The link comes from <b>no-reply@karbon360.tech</b>. If you do not see it
              shortly, please check your spam folder.
            </p>
            <div className="login-links">
              <Link to="/" className="signup-link">Back to log in</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'plan') {
    return (
      <div className="login-page">
        <AuthBranding />
        <div className="login-right">
          <div className="login-form register-form">
            <h2>Choose a plan</h2>
            <p className="register-intro">
              Pick the plan that fits. Nothing is charged now — we confirm the details
              with you first.
            </p>

            <div className="plan-options">
              {Object.entries(PLANS).map(([key, p]) => (
                <button
                  type="button"
                  key={key}
                  className={`plan-option ${plan === key ? 'selected' : ''}`}
                  onClick={() => setPlan(key)}
                  aria-pressed={plan === key}
                >
                  <span className="plan-option-head">
                    <b>{p.name}</b>
                    <span className="plan-option-price">{p.price}</span>
                  </span>
                  <span className="plan-option-blurb">{p.blurb}</span>
                </button>
              ))}
            </div>

            {error && <div className="login-error" role="alert">{error}</div>}

            <button
              type="button"
              className="login-submit"
              onClick={submit}
              disabled={submitting || !plan}
            >
              {submitting ? 'Sending…' : 'Complete registration'}
            </button>

            <button
              type="button"
              className="plan-back"
              onClick={() => setStep('details')}
              disabled={submitting}
            >
              Back to your details
            </button>

            <p className="register-note">
              Accounts are activated by our team, so you will not be able to log in
              straight away — we will email you when your workspace is ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <AuthBranding />
      <div className="login-right">
        <form className="login-form register-form" onSubmit={goToPlan} noValidate>
          <h2>Create your account</h2>
          <p className="register-intro">Tell us about you, then choose a plan.</p>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="reg-full-name">Full name</label>
              <input id="reg-full-name" name="full_name" value={form.full_name} onChange={handleChange} autoComplete="name" />
            </div>
            <div className="form-field">
              <label htmlFor="reg-age">Age</label>
              <input id="reg-age" name="age" value={form.age} onChange={handleChange} inputMode="numeric" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="reg-email">Email address</label>
              {/* autoCapitalize off: a capitalised address is one its owner
                  cannot type back at the login screen. */}
              <input id="reg-email" name="email" type="email" value={form.email} onChange={handleChange}
                autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </div>
            <div className="form-field">
              <label htmlFor="reg-phone">Phone number</label>
              <input id="reg-phone" name="phone" value={form.phone} onChange={handleChange} autoComplete="tel" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="reg-occupation">Occupation</label>
              <input id="reg-occupation" name="occupation" value={form.occupation} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label htmlFor="reg-company">Company</label>
              <input id="reg-company" name="company" value={form.company} onChange={handleChange} autoComplete="organization" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="reg-company-size">Company size</label>
              <input id="reg-company-size" name="company_size" value={form.company_size} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label htmlFor="reg-country">Country</label>
              <input id="reg-country" name="country" value={form.country} onChange={handleChange} autoComplete="country-name" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="reg-username">Username</label>
              <input id="reg-username" name="username" value={form.username} onChange={handleChange}
                autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </div>
            <div className="form-field">
              <label htmlFor="reg-password">Password</label>
              <input id="reg-password" name="password" type="password" value={form.password} onChange={handleChange} autoComplete="new-password" />
            </div>
          </div>

          <label className="consent-row" htmlFor="reg-marketing">
            <input id="reg-marketing" name="marketing_opt_in" type="checkbox"
              checked={form.marketing_opt_in} onChange={handleChange} />
            <span>Send me occasional product news and offers from Karbon360.</span>
          </label>

          {validationErrors.length > 0 && (
            <div className="login-error" role="alert">
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          )}
          {error && <div className="login-error" role="alert">{error}</div>}

          <button type="submit" className="login-submit">Continue</button>

          <div className="login-links">
            <Link to="/" className="signup-link">Already have an account? Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await axios.post(`${API_URL}/api/forgot-password`, { email });
      setMessage('If this email exists, a reset link will be sent.');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError('Error sending reset link');
    }
  };

  return (
    <div className="login-page">
      <AuthBranding />
      <div className="login-right">
        <form className="login-form" onSubmit={handleSubmit}>
          <h2>Forgot Password</h2>
          <label>Email</label>
          <input type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} />
          {error && <div style={{color: 'red', fontSize: '0.95rem'}}>{error}</div>}
          {message && <div style={{color: 'green', fontSize: '0.95rem'}}>{message}</div>}
          <button type="submit">Send Reset Link</button>
          <div className="login-links">
            <Link to="/" className="signup-link">Back to Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPassword() {
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setError(t('appx.resetLinkInvalid'));
        setValidating(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/password-reset/validate`, { params: { token } });
        if (res.data.valid) {
          setTokenValid(true);
        } else {
          setError(t('appx.resetLinkExpired'));
        }
      } catch (err) {
        setError(t('appx.resetLinkVerifyError'));
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!password || password.length < 6) {
      setError(t('appx.passwordMin'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('appx.passwordMismatch'));
      return;
    }

    try {
      await axios.post(`${API_URL}/api/password-reset/complete`, { token, password });
      setMessage(t('appx.passwordChanged'));
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('appx.resetFailed'));
    }
  };

  return (
    <div className="login-page">
      <AuthBranding />
      <div className="login-right">
        <form className="login-form" onSubmit={handleSubmit}>
          <h2>{t('appx.resetTitle')}</h2>
          {validating && <p>{t('appx.validatingLink')}</p>}
          {!validating && tokenValid && (
            <>
              <label>{t('appx.newPassword')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <label>{t('appx.confirmPassword')}</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              {error && <div style={{color: 'red', fontSize: '0.95rem'}}>{error}</div>}
              {message && <div style={{color: 'green', fontSize: '0.95rem'}}>{message}</div>}
              <button type="submit">{t('appx.savePassword')}</button>
            </>
          )}
          {!validating && !tokenValid && (
            <>
              {error && <div style={{color: 'red', fontSize: '0.95rem'}}>{error}</div>}
              <div className="login-links">
                <Link to="/" className="signup-link">{t('appx.backToLogin')}</Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

/*
 * The catch-all.
 *
 * Every unrecognised path used to Navigate to "/" with replace, which silently
 * swallowed the original URL and left the person on the login screen with no
 * idea why. An invited customer whose link arrived altered — corporate mail
 * security rewrites URLs, and clients wrap and re-wrap them — landed on login,
 * saw "Create an account", and ended up on the public signup form instead of
 * their wizard. The invite looked broken and the token stayed unused.
 *
 * A token in the query means this was an invite link however the path got
 * mangled, so honour it rather than discarding it.
 */
function UnmatchedRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  if (token) {
    return <Navigate to={`/register-wizard?token=${encodeURIComponent(token)}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function Dashboard() {
  return (
    <div style={{display:'flex',height:'100vh',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
      <h1>Welcome to Karbon360&deg; Dashboard!</h1>
      <p>You are logged in.</p>
      <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/'; }}>Logout</button>
    </div>
  );
}

// Using shared ProtectedRoute from components (handles nav via <Navigate />)

// Removed unused local Buildings component; using BuildingsPage route instead

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/signup" element={<Register />} />
        {/* the website's pricing page links here with ?plan=starter|growth|compliance */}
        <Route path="/subscribe" element={<Register />} />
        <Route path="/registration-wizard" element={<RegistrationWizard />} />
        <Route path="/register-wizard" element={<RegistrationWizard />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/badge" element={<BadgePage />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/buildings" element={<ProtectedRoute><BuildingsPage /></ProtectedRoute>} />
        {/* Admin */}
        <Route path="/admin/tokens" element={<ProtectedRoute><AdminTokensPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
        {/* Emissions Dashboard */}
        <Route path="/emissions" element={<ProtectedRoute><EmissionsDashboard /></ProtectedRoute>} />
        <Route path="/emissions/detailed" element={<ProtectedRoute><DetailedReportPage /></ProtectedRoute>} />
        <Route path="/regjistrimi" element={<ProtectedRoute><MonthlyFlowPage /></ProtectedRoute>} />
        {/* Invoices */}
        <Route path="/scope-2/electricity" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
        <Route path="/invoices-new" element={<ProtectedRoute><InvoicesPageNew /></ProtectedRoute>} />
        {/* Recipes */}
        <Route path="/recipes" element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />
        {/* Scope 1 */}
        <Route path="/scope-1/djegja-stacionare" element={<ProtectedRoute><Scope1DjegjaPage /></ProtectedRoute>} />
        <Route path="/scope-1/djegja-mobile" element={<ProtectedRoute><Scope1MobilePage /></ProtectedRoute>} />
        <Route path="/scope-1/emetimet-nga-proceset" element={<ProtectedRoute><Scope1ProcessPage /></ProtectedRoute>} />
        <Route path="/scope-1/emetimet-fugjitive" element={<ProtectedRoute><Scope1FugitivePage /></ProtectedRoute>} />
        {/* Scope 2 */}
        <Route path="/scope-2/emetimet-nga-proceset" element={<ProtectedRoute><Scope2ProcessPage /></ProtectedRoute>} />
        {/* Scope 3 */}
        <Route path="/scope-3" element={<ProtectedRoute><Scope3Page /></ProtectedRoute>} />
        {/* v2 Overview — the landing screen. /emissions is left untouched. */}
        <Route path="/overview" element={<ProtectedRoute><OverviewPage /></ProtectedRoute>} />
        <Route path="/action-inbox" element={<ProtectedRoute><ActionInboxPage /></ProtectedRoute>} />
        {/* v2 Records — the old /invoices-new register stays routed and untouched. */}
        <Route path="/records" element={<ProtectedRoute><RecordsPage /></ProtectedRoute>} />
        <Route path="/data-quality" element={<ProtectedRoute><DataQualityPage /></ProtectedRoute>} />
        <Route path="/explorer" element={<ProtectedRoute><ExplorerPage /></ProtectedRoute>} />
        {/* v2 Facilities — the old /buildings page stays routed and untouched. */}
        <Route path="/facilities" element={<ProtectedRoute><FacilitiesPage /></ProtectedRoute>} />
        {/* CBAM — routes registered for every signed-in account, per the house
            philosophy: the sidebar hides the links for non-qualifying plans,
            CbamGate renders the upsell in-page, and /api/cbam/* enforces the
            plan for real (requireCompliance). */}
        <Route path="/cbam" element={<ProtectedRoute><CbamOverviewPage /></ProtectedRoute>} />
        <Route path="/cbam/installations" element={<ProtectedRoute><CbamInstallationsPage /></ProtectedRoute>} />
        <Route path="/cbam/goods" element={<ProtectedRoute><CbamGoodsPage /></ProtectedRoute>} />
        {/* Account — reached from the account menu, not the sidebar */}
        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        {/* v2 design-system gallery — deliberately not in the navigation */}
        <Route path="/ui-kit" element={<ProtectedRoute><UiKitPage /></ProtectedRoute>} />
        {/* Anything unrecognised lands on the login screen — unless it carries a
            registration token, in which case it is a mangled invite link and the
            person is sent to the wizard instead of being dumped on login. */}
        <Route path="*" element={<UnmatchedRoute />} />
      </Routes>
    </Router>
  );
}

export default App;
